/**
 * One conversation, whole: the reconnecting banner, the thread, and the composer — or, when the
 * coaching relationship has ended, the read-only note in its place (board C10). The same pane is the
 * `/czat` screen's right column and the Zadania panel's body; the host draws the header above it,
 * because the two headers say different things (boards C1 and P2).
 *
 * It owns what belongs to ONE conversation while it is on screen: the draft (kept per conversation
 * in the browser, `model/chatDrafts`), the tray, the attachment popover, the new-messages marker and
 * the drop target. Switching conversation keeps the old draft and resets the rest.
 *
 * ── The new-messages marker (owner decision on the canon's Main board) ──────────────────────
 * A line with a dot, the number of new messages and the time of the first, standing before the
 * oldest message the trainer had not read when they opened the conversation. It stays put while
 * they read, and it leaves — fading out — when they answer.
 *
 * ── Escape ───────────────────────────────────────────────────────────────────────────────────
 * The first Escape closes the popover or the tray's preview; the host decides what a second one
 * does (`onEscape`): the panel closes, the full screen ignores it.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { preferenceStore } from '~/shared/lib/preferenceStore';
import { useSkeletonGate } from '~/shared/lib/useSkeletonGate';
import { LiveRegionAnnouncer } from '~/shared/ui/LiveRegionAnnouncer';
import skeleton from '~/shared/ui/skeleton.module.css';

import { draftOf, readDrafts, withDraft, writeDrafts, type DraftMap } from '../model/chatDrafts';
import type { ChatNotice } from '../model/chatSession';
import type { ConversationRow } from '../model/conversationRows';
import { buildThreadItems, unreadAnchor } from '../model/threadItems';
import { isReconnecting, useRealtimeStatus } from '../lib/chatSessionContext';
import { useConversation } from '../lib/useConversation';

import type { PickerKind } from './AttachPopover';
import { CameraGlyph, ClockGlyph, GreetGlyph, ImageGlyph, LayersGlyph } from './ChatGlyphs';
import { Composer, TRAY_LIMIT, trayItemsOf, type TrayItem } from './Composer';
import { ChatAvatar } from './ChatAvatar';
import { MediaLightbox } from './MediaLightbox';
import { MessageThread } from './MessageThread';
import type { MediaView } from './MessageBubble';
import styles from './chat.module.css';

const DIVIDER_LEAVE_MS = 460;

export interface ConversationPaneProps {
  readonly conversation: ConversationRow;
  /** The unread count the inbox showed when the trainer opened this conversation. */
  readonly unreadAtOpen: number;
  readonly variant: 'screen' | 'dock';
  readonly onOpenClip: (clipId: number) => void;
  /** Escape with nothing of the pane's own open. */
  readonly onEscape?: () => void;
  /** Focus the field when the pane opens — the panel does, the full screen does on a pick. */
  readonly autoFocus: boolean;
  /** A draft handed over from the other surface (the panel's „Otwórz w Czacie"). */
  readonly initialPicker?: PickerKind | null;
}

let draftMemory: DraftMap | null = null;

function drafts(): DraftMap {
  if (draftMemory === null) draftMemory = readDrafts(preferenceStore());
  return draftMemory;
}

function saveDraft(conversationId: number, text: string): void {
  draftMemory = withDraft(drafts(), conversationId, text);
  writeDrafts(preferenceStore(), draftMemory);
}

/** The unsent text of a conversation, for the inbox row's „Szkic: …". */
export function conversationDraft(conversationId: number): string {
  return draftOf(drafts(), conversationId);
}

export function ConversationPane({
  conversation,
  unreadAtOpen,
  variant,
  onOpenClip,
  onEscape,
  autoFocus,
  initialPicker,
}: ConversationPaneProps) {
  const { t } = useTranslation();
  const id = conversation.id;
  const [announcement, setAnnouncement] = useState('');
  const onNotice = useCallback(
    (notice: ChatNotice) => {
      setAnnouncement(t(notice.key, notice.params));
    },
    [t],
  );
  const thread = useConversation(id, onNotice);
  const status = useRealtimeStatus();
  const typing = thread.typing;
  const gate = useSkeletonGate(thread.pending);

  const [draft, setDraft] = useState(() => draftOf(drafts(), id));
  const [tray, setTray] = useState<readonly TrayItem[]>([]);
  const [attach, setAttach] = useState<{ open: boolean; picker: PickerKind | null }>(() => ({
    open: initialPicker !== undefined && initialPicker !== null,
    picker: initialPicker ?? null,
  }));
  const [lightbox, setLightbox] = useState<MediaView | null>(null);
  const [dropActive, setDropActive] = useState(false);
  const [scrollSignal, setScrollSignal] = useState(0);
  const [anchor, setAnchor] = useState<{ for: number; key: string | null; leaving: boolean } | null>(null);
  const field = useRef<HTMLTextAreaElement>(null);
  const [now] = useState(() => new Date());

  // Placed once per opening, when the first page has landed.
  if (anchor?.for !== id && !thread.pending) {
    setAnchor({ for: id, key: unreadAnchor(thread.messages, unreadAtOpen), leaving: false });
  }
  const anchorKey = anchor?.for === id ? anchor.key : null;

  const items = useMemo(() => buildThreadItems(thread.messages, anchorKey, now), [thread.messages, anchorKey, now]);

  useEffect(() => {
    if (autoFocus) field.current?.focus();
  }, [autoFocus, id]);

  useEffect(() => {
    if (anchor?.leaving !== true) return undefined;
    const timer = setTimeout(() => {
      setAnchor((current) => (current === null ? current : { ...current, key: null, leaving: false }));
    }, DIVIDER_LEAVE_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [anchor?.leaving]);

  const onDraft = (text: string) => {
    setDraft(text);
    saveDraft(id, text);
  };

  const onSent = () => {
    setScrollSignal((value) => value + 1);
    if (anchorKey !== null) setAnchor((current) => (current === null ? current : { ...current, leaving: true }));
    field.current?.focus();
  };

  const onAttach = (open: boolean, picker: PickerKind | null) => {
    setAttach({ open, picker });
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Escape' || event.defaultPrevented) return;
    if (attach.open) {
      event.preventDefault();
      setAttach({ open: false, picker: null });
      field.current?.focus();
      return;
    }
    if (dropActive) {
      setDropActive(false);
      return;
    }
    onEscape?.();
  };

  const hasFiles = (event: DragEvent) => Array.from(event.dataTransfer.types).includes('Files');

  const onDragOver = (event: DragEvent) => {
    if (!conversation.canSend || !hasFiles(event)) return;
    event.preventDefault();
    if (!dropActive) setDropActive(true);
  };

  const onDrop = (event: DragEvent) => {
    event.preventDefault();
    setDropActive(false);
    const files = Array.from(event.dataTransfer.files);
    const room = TRAY_LIMIT - tray.length;
    const items = trayItemsOf(files.slice(0, Math.max(0, room)));
    if (files.length > room) setAnnouncement(t('web:chat.tray.tooMany', { count: TRAY_LIMIT }));
    if (items.length > 0) setTray([...tray, ...items]);
    field.current?.focus();
  };

  const reconnecting = isReconnecting(status);
  const empty = !gate.waiting && !thread.failed && thread.messages.length === 0;
  const firstName = conversation.name.split(' ')[0] ?? conversation.name;

  return (
    <div className={`${styles.pane} ${variant === 'dock' ? styles.paneDock : ''}`} onKeyDown={onKeyDown}>
      <LiveRegionAnnouncer message={announcement} />
      {reconnecting ? (
        <div className={styles.banner} role="status">
          <ClockGlyph size={14} width={2} />
          {t('chat.window.reconnectBanner')}
        </div>
      ) : null}

      {gate.waiting ? (
        <div className={styles.threadSkeleton} aria-busy="true">
          <span className={styles.sr}>{t('web:chat.thread.loading')}</span>
          {gate.skeleton
            ? (['theirs w60', 'theirs w40', 'mine w50', 'card', 'mine w30', 'theirs w70'] as const).map((shape) => (
                <span key={shape} className={`${skeleton.bar} ${styles.skeletonBubble} ${shapeClass(shape)}`} />
              ))
            : null}
        </div>
      ) : thread.failed ? (
        <div className={`${styles.threadState} ${gate.reveal}`}>
          <b>{t('chat.window.errorTitle')}</b>
          <span>{t('chat.window.errorBody')}</span>
          <button className={styles.starter} type="button" onClick={thread.refetch}>
            {t('chat.window.retry')}
          </button>
        </div>
      ) : empty ? (
        <div className={`${styles.threadEmpty} ${gate.reveal}`} onDragOver={onDragOver}>
          <ChatAvatar initials={conversation.initials} avatarUrl={conversation.avatarUrl} size={variant === 'dock' ? 60 : 64} />
          <h2>{t('web:chat.thread.start.title', { name: conversation.name })}</h2>
          <p>{t('web:chat.thread.start.body')}</p>
          {conversation.canSend ? (
            <div className={styles.starters}>
              <button
                className={styles.starter}
                type="button"
                onClick={() => {
                  onDraft(t('web:chat.thread.start.greeting', { name: firstName }));
                  field.current?.focus();
                }}
              >
                <GreetGlyph />
                {t('web:chat.thread.start.greet')}
              </button>
              {conversation.customerId !== null ? (
                <>
                  <button className={styles.starter} type="button" onClick={() => { onAttach(true, 'plan'); }}>
                    <LayersGlyph size={15} />
                    {t('web:chat.thread.start.sendPlan')}
                  </button>
                  <button className={styles.starter} type="button" onClick={() => { onAttach(true, 'clipRequest'); }}>
                    <CameraGlyph size={15} />
                    {t('web:chat.thread.start.askClip')}
                  </button>
                </>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : (
        <div className={`${styles.threadHost} ${gate.reveal}`}>
          <MessageThread
            conversationId={id}
            items={items}
            typingName={typing ? firstName : null}
            hasOlder={thread.hasOlder}
            loadingOlder={thread.loadingOlder}
            onLoadOlder={thread.loadOlder}
            customerId={conversation.customerId}
            theirName={conversation.name}
            dividerLeaving={anchor?.leaving === true}
            scrollToEndSignal={scrollSignal}
            onOpenMedia={setLightbox}
            onOpenClip={onOpenClip}
            onRetry={thread.retry}
            onRemove={thread.remove}
            onDragOver={onDragOver}
          />
        </div>
      )}

      {conversation.canSend ? (
        <Composer
          customerId={conversation.customerId}
          draft={draft}
          onDraft={onDraft}
          tray={tray}
          onTray={setTray}
          attachOpen={attach.open}
          picker={attach.picker}
          onAttach={onAttach}
          send={thread.send}
          onTyped={thread.typed}
          onSent={onSent}
          onAnnounce={setAnnouncement}
          fieldRef={field}
        />
      ) : (
        <div className={styles.frozen} role="status">
          <b>{t('coaching.chat.frozenTitle')}</b>
          <span>{t('coaching.chat.frozenTrainer')}</span>
        </div>
      )}

      {dropActive ? (
        <div
          className={styles.drop}
          onDragOver={onDragOver}
          onDragLeave={(event) => {
            if (event.currentTarget === event.target) setDropActive(false);
          }}
          onDrop={onDrop}
        >
          <div className={styles.dropBox}>
            <ImageGlyph size={28} />
            <div className={styles.dropTitle}>{t('web:chat.drop.title')}</div>
            <div className={styles.dropHint}>{t('web:chat.drop.hint')}</div>
          </div>
        </div>
      ) : null}

      <MediaLightbox
        view={lightbox}
        onClose={() => {
          setLightbox(null);
        }}
      />
    </div>
  );
}

function shapeClass(shape: 'theirs w60' | 'theirs w40' | 'mine w50' | 'card' | 'mine w30' | 'theirs w70'): string {
  switch (shape) {
    case 'theirs w60':
      return `${styles.skTheirs} ${styles.w60}`;
    case 'theirs w40':
      return `${styles.skTheirs} ${styles.w40}`;
    case 'mine w50':
      return `${styles.skMine} ${styles.w50}`;
    case 'card':
      return `${styles.skTheirs} ${styles.skCard}`;
    case 'mine w30':
      return `${styles.skMine} ${styles.w30}`;
    case 'theirs w70':
      return `${styles.skTheirs} ${styles.w70}`;
  }
}
